import { Row, Col } from 'antd';
import { useNavigate } from 'react-router-dom';
import {
    RiseOutlined,
    WalletOutlined,
    UserOutlined,
    AlertOutlined,
    HistoryOutlined,
    SoundOutlined,
    FileTextOutlined,
    CheckCircleOutlined,
    ClockCircleOutlined,
    ExclamationCircleOutlined
} from '@ant-design/icons';
import { KPICard } from './KPICard';
import { useDashboardKpis } from '../hooks/useDashboardQueries';
import { formatMoney, formatNumber } from '../utils';
import { Spin, Alert } from 'antd';

export const KPISection1 = ({ dateRange, compareDateRange, isComparing, compareLabel }: any) => {
    const navigate = useNavigate();

    const params = {
        startDate: dateRange?.[0]?.format('YYYY-MM-DD'),
        endDate: dateRange?.[1]?.format('YYYY-MM-DD'),
        timezone: 'Asia/Kolkata',
    };

    const { data: response, isLoading, isError } = useDashboardKpis(params);

    if (isLoading) return <div style={{ textAlign: 'center', padding: 50 }}><Spin size="large" /></div>;
    if (isError) return <Alert type="error" message="Failed to load KPIs" style={{ marginBottom: 16 }} />;

    const period = response?.data?.periodMetrics;
    const snapshot = response?.data?.snapshotMetrics;

    const findBreakdown = (type: string) => period?.grossRevenueBreakdown?.find(b => b.type === type)?.amount;
    return (
        <Row gutter={[16, 16]}>
            <Col span={4}>
                <KPICard isComparing={isComparing} compareLabel={compareLabel}
                    title="Gross Revenue"
                    tooltip="Total revenue generated across all commercial channels."
                    prefix="₹ "
                    value={formatMoney(period?.grossRevenue)}
                    suffixText="18.6%"
                    isUp={true}
                    prefixIcon={<RiseOutlined />}
                    iconColor="#7c3aed"
                    bgColor="#f3e8ff"
                    onClick={() => navigate('/reports/revenue')}
                    subMetrics={[
                        { label: 'Merchant Rev', value: '₹' + formatMoney(findBreakdown('COMMISSION')), color: '#1890ff' },
                        { label: 'Sponsor Rev', value: '₹' + formatMoney(findBreakdown('SPONSORED_AD')), color: '#eb2f96' }
                    ]}
                />
            </Col>
            <Col span={4}>
                <KPICard isComparing={isComparing} compareLabel={compareLabel}
                    title="Total Wallet Balance"
                    tooltip="Combined wallet balance of all merchants. This prepaid pool represents a merchant liability (customer funds), NOT platform revenue."
                    prefix="₹ "
                    value={formatMoney(snapshot?.walletBalance)}
                    suffixText="12.4%"
                    isUp={true}
                    prefixIcon={<WalletOutlined />}
                    iconColor="#1890ff"
                    bgColor="#e6f7ff"
                    onClick={() => navigate('/reports/wallets')}
                    subMetrics={[
                        { label: 'Active Accts', value: '₹' + formatMoney(snapshot?.walletBalance), color: '#1890ff' },
                        { label: 'Dormant', value: '₹0.00', color: '#8c8c8c' }
                    ]}
                />
            </Col>
            <Col span={4}>
                <KPICard isComparing={isComparing} compareLabel={compareLabel}
                    title="Total Merchants"
                    value={formatNumber(snapshot?.totalMerchants)}
                    suffixText="8.7%"
                    isUp={true}
                    prefixIcon={<UserOutlined />}
                    iconColor="#52c41a"
                    bgColor="#f6ffed"
                    onClick={() => navigate('/merchants')}
                    subMetrics={[
                        { label: 'Active', value: formatNumber(snapshot?.totalMerchants), color: '#52c41a' },
                        { label: 'New', value: '124', color: '#1890ff' },
                        { label: 'Inactive', value: '182', color: '#fa8c16' },
                        { label: 'Suspended', value: '46', color: '#ff4d4f' }
                    ]}
                />
            </Col>
            <Col span={4}>
                <KPICard isComparing={isComparing} compareLabel={compareLabel}
                    title="Low Balance Merchants"
                    tooltip="Merchants with wallet balance < ₹2,000. They risk service interruption for leads, radius, users, and commissions."
                    value={formatNumber(snapshot?.lowBalanceMerchants)}
                    suffixText="5.1%"
                    isUp={false}
                    prefixIcon={<AlertOutlined />}
                    iconColor="#fa8c16"
                    bgColor="#fff7e6"
                    onClick={() => navigate('/merchants?status=low_balance')}
                    subMetrics={[
                        { label: 'Warning', value: '22', color: '#fa8c16' },
                        { label: 'Critical', value: '15', color: '#ff4d4f' }
                    ]}
                />
            </Col>
            <Col span={4}>
                <KPICard isComparing={isComparing} compareLabel={compareLabel}
                    title="Wallet Recharges"
                    tooltip="Total money added to merchant wallets. This measures cash inflow, not necessarily platform revenue."
                    prefix="₹ "
                    value={formatMoney(period?.walletRecharges)}
                    suffixText="24.5%"
                    isUp={true}
                    prefixIcon={<HistoryOutlined />}
                    iconColor="#1890ff"
                    bgColor="#e6f7ff"
                    onClick={() => navigate('/reports/recharges')}
                    subMetrics={[
                        { label: 'UPI', value: '₹' + formatMoney(period?.walletRecharges), color: '#1890ff' },
                        { label: 'Cards', value: '₹0.00', color: '#52c41a' },
                        { label: 'Net Bank', value: '₹0.00', color: '#fa8c16' },
                        { label: 'Other', value: '₹0.00', color: '#8c8c8c' }
                    ]}
                />
            </Col>
            <Col span={4}>
                <KPICard isComparing={isComparing} compareLabel={compareLabel}
                    title="Sponsored Ad Revenue"
                    tooltip="Actual revenue collected from sponsors for advertising. This is completely distinct from Merchant Revenue."
                    prefix="₹ "
                    value={formatMoney(period?.sponsoredAdRevenue)}
                    suffixText="32.7%"
                    isUp={true}
                    prefixIcon={<SoundOutlined />}
                    iconColor="#eb2f96"
                    bgColor="#fff0f6"
                    onClick={() => navigate('/reports/ads')}
                    subMetrics={[
                        { label: 'Search Ads', value: '₹' + formatMoney(period?.sponsoredAdRevenue), color: '#eb2f96' },
                        { label: 'Banners', value: '₹0.00', color: '#722ed1' }
                    ]}
                />
            </Col>
        </Row>
    );
};

export const KPISection2 = ({ isComparing, compareLabel }: any) => {
    return (
        <Row gutter={[16, 16]}>
            <Col flex={1}>
                <KPICard isComparing={isComparing} compareLabel={compareLabel} title="Total Invoices" tooltip="The total number of invoices generated in the selected period." value="1,245" suffixText="15.3%" isUp={true} prefixIcon={<FileTextOutlined />} iconColor="#52c41a" bgColor="#f6ffed" />
            </Col>
            <Col flex={1}>
                <KPICard isComparing={isComparing} compareLabel={compareLabel} title="Paid / Auto-Debited" tooltip="Number of invoices that have been successfully paid via immediate or auto-debit." value="1,102" suffixText="16.8%" isUp={true} prefixIcon={<CheckCircleOutlined />} iconColor="#1890ff" bgColor="#e6f7ff" />
            </Col>
            <Col flex={1}>
                <KPICard isComparing={isComparing} compareLabel={compareLabel} title="Failed Debits (In Grace)" tooltip="Number of invoices waiting for payment. Merchant is within the 3-day grace period." value="96" suffixText="8.2%" isUp={false} prefixIcon={<ClockCircleOutlined />} iconColor="#fa8c16" bgColor="#fff7e6" />
            </Col>
            <Col flex={1}>
                <KPICard isComparing={isComparing} compareLabel={compareLabel} title="Defaults & Downgrades" tooltip="Number of invoices that have passed their due date (3 days). Subscriptions are downgraded and pro-rata debited." value="47" suffixText="12.5%" isUp={false} prefixIcon={<ExclamationCircleOutlined />} iconColor="#ff4d4f" bgColor="#fff2f0" />
            </Col>
            <Col flex={1}>
                <KPICard isComparing={isComparing} compareLabel={compareLabel} title="Total Invoice Amount" tooltip="The total monetary value of all invoices combined." prefix="₹ " value="15,60,300.00" suffixText="19.4%" isUp={true} prefixIcon={<WalletOutlined />} iconColor="#7c3aed" bgColor="#f3e8ff" />
            </Col>
        </Row>
    );
};
